// Unpublished Work (c) 2023-2024 Deere & Company.

import { AxiosResponse } from "axios";
import { PromotionDownloadStatusesResponse } from "../../hooks/usePromotionDownloadStatus/usePromotionDownloadStatus";
import { PromotionsDetails } from "../../pages/promotionsV2/PromotionDetails/type";
import { Attachment, IPromotion } from "../../types";
import { BlobResponse } from "../../utils/downloadFile";
import { PendingPromotionCompilationJob } from "../../utils/promotions/promotionUtils";
import axios from "../api";
import {
  Attachments,
  PartMappingRequest,
  PartsMappingResponse,
  PartsMappingResponseV2,
  Promotions,
  PromotionType,
} from "./types";

const promotionsApi = {
  getPromotionsList: async (
    promotionsServiceUrl: string,
    accountId: string,
    token: string,
    lang: string,
    partNumber?: string,
    enableFutureTpm?: boolean
  ): Promise<IPromotion[]> => {
    let queryParameters = `accountId=${accountId}&lang=${lang}`;
    if (partNumber) queryParameters += `&partNumber=${partNumber}`;
    queryParameters += enableFutureTpm ? `&enableFutureTpm=true` : "";
    const response = await axios.getWithRetries<Promotions>(
      `${promotionsServiceUrl}/tradepromotions/v1/search?${queryParameters}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.promotions;
  },

  getPromotions: async (
    url: string,
    accountIds: string[],
    token: string,
    language: string = "en_US",
    promotionType?: PromotionType,
    enableFutureTpm?: boolean
  ): Promise<IPromotion[]> => {
    const apiUrl = `${url}/tradepromotions/v2/search${
      enableFutureTpm ? "?enableFutureTpm=true" : ""
    }`;
    const response = await axios.postWithRetries<Promotions>(
      apiUrl,
      {
        accountIds,
        language,
        promotionType,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.promotions;
  },

  getAttachments: (
    promotionsServiceUrl: string,
    promotionId: string,
    token: string
  ): Promise<Attachment[]> =>
    axios
      .getWithRetries<Attachments>(
        `${promotionsServiceUrl}/tradepromotions/v1/${promotionId}/attachments`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => response.data.attachments),

  getPromotionDetails: (
    promotionsServiceUrl: string,
    accountId: string,
    promotionId: string,
    language: string,
    token: string,
    enableFutureTpm?: boolean
  ): Promise<PromotionsDetails> => {
    const url = `${promotionsServiceUrl}/tradepromotions/v1/${promotionId}?accountId=${accountId}&lang=${language}${
      enableFutureTpm ? "&enableFutureTpm=true" : ""
    }`;
    return axios
      .getWithRetries<PromotionsDetails>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data);
  },

  getPartsMapping: (
    promotionsServiceUrl: string,
    promotionId: string,
    params: string[],
    token: string
  ): Promise<PartsMappingResponse> =>
    axios
      .getWithRetries<PartsMappingResponse>(
        `${promotionsServiceUrl}/tradepromotions/v1/${promotionId}/part-mapping?${params.join(
          "&"
        )}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => response.data),

  getPartsMappingV2: (
    promotionsServiceUrl: string,
    { promotionId, queryParams }: PartMappingRequest,
    token: string
  ): Promise<PartsMappingResponseV2> => {
    type PartsMappingQueryParams = PartMappingRequest["queryParams"];
    const query = Object.keys(queryParams)
      .filter(
        (key) =>
          queryParams[key as keyof PartsMappingQueryParams] !== undefined &&
          queryParams[key as keyof PartsMappingQueryParams] !== ""
      )
      .map(
        (key) => `${key}=${queryParams[key as keyof PartsMappingQueryParams]}`
      )
      .join("&");

    return axios
      .getWithRetries<PartsMappingResponseV2>(
        `${promotionsServiceUrl}/tradepromotions/v1/${promotionId}/part-mapping?${query}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => response.data);
  },

  getPartsMappingWithFilters: (
    promotionsServiceUrl: string,
    { promotionId, queryParams }: PartMappingRequest,
    token: string
  ): Promise<PartsMappingResponse> => {
    type PartsMappingQueryParams = PartMappingRequest["queryParams"];
    const query = Object.keys(queryParams)
      .filter(
        (key) =>
          queryParams[key as keyof PartsMappingQueryParams] !== undefined &&
          queryParams[key as keyof PartsMappingQueryParams] !== ""
      )
      .map(
        (key) => `${key}=${queryParams[key as keyof PartsMappingQueryParams]}`
      )
      .join("&");

    return axios
      .getWithRetries<PartsMappingResponse>(
        `${promotionsServiceUrl}/tradepromotions/v1/${promotionId}/part-mapping?${query}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => response.data);
  },

  downloadPromotionsListExcel: (
    promotionsServiceUrl: string,
    accountId: string,
    lang: string,
    token: string,
    enableFutureTpm?: boolean
  ): Promise<AxiosResponse<BlobResponse>> => {
    const url = `${promotionsServiceUrl}/tradepromotions/v1/all-promotions/excel/download?accountId=${accountId}&lang=${lang}${
      enableFutureTpm ? "&enableFutureTpm=true" : ""
    }`;
    return axios.getWithRetries<BlobResponse>(url, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  downloadMultipleAccountsPromotionsExcel: (
    promotionsServiceUrl: string,
    accountIds: string[],
    language: string,
    token: string,
    enableFutureTpm?: boolean
  ): Promise<AxiosResponse<BlobResponse>> => {
    const url = `${promotionsServiceUrl}/tradepromotions/v1/all-promotions/excel/download`;
    return axios.postWithRetries<BlobResponse>(
      url,
      {
        accountIds,
        language,
        enableFutureTpm: enableFutureTpm ?? false,
      },
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },

  downloadPartsMappingExcel: (
    promotionsServiceUrl: string,
    promotionId: string,
    accountId: string,
    language: string,
    totalParts: number,
    token: string,
    hideDiscountAndCriteriaColumns?: boolean,
    showLineLevelScaleColumns?: boolean,
    locale?: string
  ): Promise<AxiosResponse<BlobResponse>> => {
    const params = new URLSearchParams({
      accountId,
      lang: language,
      totalParts: String(totalParts),
    });
    if (hideDiscountAndCriteriaColumns) {
      params.append("HideColumns", "true");
    }
    if (showLineLevelScaleColumns) {
      params.append("ShowLineLevelScaleColumns", "true");
    }
    if (locale) {
      params.append("locale", locale);
    }
    return axios.getWithRetries<BlobResponse>(
      `${promotionsServiceUrl}/tradepromotions/v1/${promotionId}/excel/download?${params.toString()}`,
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },

  invokeDownloadExcel: (
    promotionsServiceUrl: string,
    promotionId: string,
    accountId: string,
    language: string,
    token: string,
    scalesExist?: boolean,
    enableAsyncDownloadWithScales?: boolean,
    hideDiscountAndCriteriaColumns?: boolean,
    showLineLevelScaleColumns?: boolean
  ): Promise<AxiosResponse<PendingPromotionCompilationJob>> => {
    const params = new URLSearchParams({
      accountId,
      lang: language,
    });

    if (enableAsyncDownloadWithScales) {
      const scalesExistValue = scalesExist ?? false;
      params.append("scalesExist", String(scalesExistValue));
    }
    if (hideDiscountAndCriteriaColumns) {
      params.append("HideColumns", "true");
    }
    if (showLineLevelScaleColumns) {
      params.append("ShowLineLevelScaleColumns", "true");
    }
    return axios.getWithRetries<PendingPromotionCompilationJob>(
      `${promotionsServiceUrl}/tradepromotions/v1/${promotionId}/invoke/download?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },

  getExcelCompilationStatuses: (
    promotionsServiceUrl: string,
    accountId: string,
    jobIds: string[],
    token: string
  ): Promise<PromotionDownloadStatusesResponse> =>
    axios
      .postWithRetries<PromotionDownloadStatusesResponse>(
        `${promotionsServiceUrl}/tradepromotions/v1/excel-compilation-status?accountId=${accountId}`,
        { jobIds },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((res) => res.data),

  generateS3AttachmentUrl: async (
    promotionsServiceUrl: string,
    accessToken: string,
    jobId: string
  ): Promise<string> =>
    axios
      .getWithRetries<string>(
        `${promotionsServiceUrl}/tradepromotions/v1/promotions/${jobId}/attachment-url`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )
      .then(({ data }) => data),

  getDiscountScales: (
    promotionsServiceUrl: string,
    promotionId: string,
    token: string
  ): Promise<unknown> =>
    axios
      .getWithRetries<unknown>(
        `${promotionsServiceUrl}/tradepromotions/v1/${promotionId}/discount-scale`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => response.data),
};

export default promotionsApi;
